import AIcon from '@/components/A/AIcon/AIcon'
import SubAnalysisDescriptionPopover from '../ProjectDetailPage/components/VideoAnalysis/SubAnalysisDescriptionPopover/SubAnalysisDescriptionPopover'
import styles from './StatisticPage.module.less'
import cls from 'classnames'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useRequest } from 'alova/client'
import { api } from '@/api'
import { Spin } from 'antd'
import { commonUtils } from '@/helpers/commonUtils'
import { formatRatioAsPercentValue } from '@/helpers/percent'
import { observer } from 'mobx-react'
import { corpService } from '@/services/corpService'
import useProductType from '@/helpers/commonHooks/useProductType'

const processPercent = (ratio: number, fixed: number = 1) => {
  const percent = (ratio * 100).toFixed(fixed)
  if (percent === '0.0') {
    return '0%'
  }
  return `${percent}%`
}

const normalizeNum = (num: number | typeof NaN) => {
  if (isNaN(num)) {
    return 0
  }
  return num
}

function StatisticPage() {
  const { t } = useTranslation()
  const [mode, setMode] = useState<'mode-1' | 'mode-2'>('mode-1')
  const { productType } = useProductType()
  const { data, loading } = useRequest(api.statistic.getStatistic(productType))
  // const { data: { brandNormStatus } } = useRequest(api.corp.getCorpBrandNormStatus(), { initialData: { brandNormStatus: false } })
  // const { brandNormStatus } = corpService.corpInfo!
  const [brandNormStatus, setBrandNormStatus] = useState(false)
  const [statisticData, setStatisticData] = useState<Models.StatisticData>()
  const [scoreList, setScoreList] = useState<number[]>([])
  const [brandScoreList, setBrandScoreList] = useState<number[]>([])
  const [chartData, setChartData] = useState<{ name: string; value: number; brandValue: number, percent: string, brandPercent: string }[]>([])
  const [ratio, setRatio] = useState(0)
  const [interval, setInterval] = useState<Array<number[]>>([])
  const CHAR_HEIGHT = 350
  const DELTA = 0.85
  const [desc1, setDesc1] = useState<{ title: string; description: string }[]>([])
  const desc2 = [
    {
      title: t('dashboard.dataDetailPage.advertisingEffectivenessIndexDistribution'),
      description: t('dashboard.dataDetailPage.advertisingEffectivenessIndexDistributionDesc'),
    },
  ]

  useEffect(() => {
    if (data) {
      const { data: _data, scoreResult } = data
      const { baseScoreList, brandScoreList } = scoreResult
      setStatisticData(_data)
      setScoreList(baseScoreList)
      setBrandScoreList(brandScoreList)
    }
  }, [data])

  useEffect(() => {
    const lessThen40 = [scoreList.filter((item) => item < 40), brandScoreList.filter((item) => item < 40)]
    const between40And50 = [scoreList.filter((item) => item >= 40 && item < 50), brandScoreList.filter((item) => item >= 40 && item < 50)]
    const between50And60 = [scoreList.filter((item) => item >= 50 && item < 60), brandScoreList.filter((item) => item >= 50 && item < 60)]
    const between60And70 = [scoreList.filter((item) => item >= 60 && item < 70), brandScoreList.filter((item) => item >= 60 && item < 70)]
    const between70And80 = [scoreList.filter((item) => item >= 70 && item < 80), brandScoreList.filter((item) => item >= 70 && item < 80)]
    const between80And90 = [scoreList.filter((item) => item >= 80 && item < 90), brandScoreList.filter((item) => item >= 80 && item < 90)]
    const between90And100 = [scoreList.filter((item) => item >= 90 && item <= 100), brandScoreList.filter((item) => item >= 90 && item <= 100)]
    const _chartData = [
      {
        name: '<40',
        value: normalizeNum(lessThen40[0].length / scoreList.length),
        brandValue: normalizeNum(lessThen40[1].length / brandScoreList.length),
        percent: processPercent(normalizeNum(lessThen40[0].length / scoreList.length)),
        brandPercent: processPercent(normalizeNum(lessThen40[1].length / brandScoreList.length)),
      },
      {
        name: '40-50',
        value: normalizeNum(between40And50[0].length / scoreList.length),
        brandValue: normalizeNum(between40And50[1].length / brandScoreList.length),
        percent: processPercent(normalizeNum(between40And50[0].length / scoreList.length)),
        brandPercent: processPercent(normalizeNum(between40And50[1].length / brandScoreList.length)),
      },
      {
        name: '50-60',
        value: normalizeNum(between50And60[0].length / scoreList.length),
        brandValue: normalizeNum(between50And60[1].length / brandScoreList.length),
        percent: processPercent(normalizeNum(between50And60[0].length / scoreList.length)),
        brandPercent: processPercent(normalizeNum(between50And60[1].length / brandScoreList.length)),
      },
      {
        name: '60-70',
        value: normalizeNum(between60And70[0].length / scoreList.length),
        brandValue: normalizeNum(between60And70[1].length / brandScoreList.length),
        percent: processPercent(normalizeNum(between60And70[0].length / scoreList.length)),
        brandPercent: processPercent(normalizeNum(between60And70[1].length / brandScoreList.length)),
      },
      {
        name: '70-80',
        value: normalizeNum(between70And80[0].length / scoreList.length),
        brandValue: normalizeNum(between70And80[1].length / brandScoreList.length),
        percent: processPercent(normalizeNum(between70And80[0].length / scoreList.length)),
        brandPercent: processPercent(normalizeNum(between70And80[1].length / brandScoreList.length)),
      },
      {
        name: '80-90',
        value: normalizeNum(between80And90[0].length / scoreList.length),
        brandValue: normalizeNum(between80And90[1].length / brandScoreList.length),
        percent: processPercent(normalizeNum(between80And90[0].length / scoreList.length)),
        brandPercent: processPercent(normalizeNum(between80And90[1].length / brandScoreList.length)),
      },
      {
        name: '90-100',
        value: normalizeNum(between90And100[0].length / scoreList.length),
        brandValue: normalizeNum(between90And100[1].length / brandScoreList.length),
        percent: processPercent(normalizeNum(between90And100[0].length / scoreList.length)),
        brandPercent: processPercent(normalizeNum(between90And100[1].length / brandScoreList.length)),
      },
    ]
    setChartData(_chartData)
    const max1 = Math.max(..._chartData.map((item) => item.value))
    const max2 = Math.max(..._chartData.map((item) => item.brandValue))
    const max = Math.max(max1, max2)
    const tmpYAsis = (Math.floor(max * 10) + 1) / 10
    const maxYAsis = tmpYAsis > 1 ? 1 : tmpYAsis
    const ratio = (CHAR_HEIGHT / maxYAsis) * DELTA
    setRatio(ratio)
    const delta = maxYAsis / 5
    setInterval([
      [0, delta],
      [delta, delta * 2],
      [delta * 2, delta * 3],
      [delta * 3, delta * 4],
      [delta * 4, maxYAsis],
    ])
  }, [scoreList, brandScoreList])

  useEffect(() => {
    if (mode === 'mode-1') {
      setDesc1([
        {
          title: t('dashboard.dataDetailPage.totalProjects'),
          description: t('dashboard.dataDetailPage.totalProjectsDesc'),
        },
        {
          title: t('dashboard.dataDetailPage.localNorm'),
          description: t('dashboard.dataDetailPage.localNormDesc'),
        },
        {
          title: t('dashboard.dataDetailPage.projectPassRate'),
          description: t('dashboard.dataDetailPage.projectPassRateDesc'),
        },
      ])
    }
    if (mode === 'mode-2') {
      setDesc1([
        {
          title: t('dashboard.dataDetailPage.totalProjects'),
          description: t('dashboard.dataDetailPage.totalProjectsDesc'),
        },
        {
          title: t('dashboard.dataDetailPage.localNorm'),
          description: t('dashboard.dataDetailPage.localNormDesc'),
        },
        {
          title: t('dashboard.dataDetailPage.brandNorm'),
          description: t('dashboard.dataDetailPage.brandNormDesc')
        },
        {
          title: t('dashboard.dataDetailPage.projectPassRate'),
          description: t('dashboard.dataDetailPage.projectPassRateDesc'),
        },
      ])
    }
  }, [mode])

  useEffect(() => {
    if (corpService.corpInfo) {
      const brandNormStatus = corpService.corpInfo.brandNormStatus
      setBrandNormStatus(brandNormStatus)
      if (brandNormStatus) {
        setMode('mode-2')
      } else {
        setMode('mode-1')
      }
    }
  }, [corpService.corpInfo])

  return (
    <div className={styles.statisticPage} data-testid='statistic-page'>
      <div className={styles.statisticPageContent}>
        {loading && (
          <div className={styles.loading}>
            <Spin />
          </div>
        )}
        {!loading && statisticData && (
          <>
            <div className={styles.baseData}>
              <div className={styles.baseDataTitle}>
                <span>{t('dashboard.dataDetailPage.baseData')}</span>
                <SubAnalysisDescriptionPopover descriptions={desc1} />
              </div>
              <div className={styles.baseDataContent}>
                {mode === 'mode-1' && (
                  <>
                    <BaseDataItem
                      type='icon-fill-global_Projects'
                      value={statisticData.projectCount}
                      desc={t('dashboard.dataDetailPage.totalProjects')}
                    />
                    <BaseDataItem
                      type='icon-fill-global_norm'
                      value={statisticData.base.default.norm === 0 ? '-' : statisticData.base.default.norm}
                      desc={t('dashboard.dataDetailPage.localNorm')}
                    />
                    <BaseDataItem
                      type='icon-fill-global_PassRate'
                      value={
                        <span>
                          {formatRatioAsPercentValue(statisticData.base.default.passRatio)}
                          <span
                            style={{
                              fontWeight: 400,
                              fontSize: 16,
                              color: '#2A2C3F',
                              lineHeight: '26px',
                            }}
                          >
                            %
                          </span>
                        </span>
                      }
                      desc={t('dashboard.dataDetailPage.projectPassRate')}
                    />
                  </>
                )}
                {mode === 'mode-2' && (
                  <div className={cls(styles.modeContent, styles.flex)}>
                    <div className={styles.modeContentItem} style={{ marginRight: 20 }}>
                      <img
                        className={styles.contentItemBg}
                        src={commonUtils.getImagePath('statisticPage/item-bg-2.png')}
                        alt=''
                      />
                      <AIcon width={40} height={40} type='icon-fill-global_Projects' />
                      <p className={styles.itemValue}>{statisticData.projectCount}</p>
                      <p className={styles.itemDesc}>
                        {t('dashboard.dataDetailPage.totalProjects')}
                      </p>
                    </div>
                    <div className={styles.modeContentPanel}>
                      <img
                        className={styles.panelContentBg}
                        src={commonUtils.getImagePath('statisticPage/item-bg-3.png')}
                        alt=''
                      />
                      <div className={styles.modeContentPanelItem}>
                        <div className={cls(styles.panelItem)}>
                          <div className={styles.itemContent}>
                            <p className={styles.itemValue}>{statisticData.base.default.projects}</p>
                            <p className={styles.itemDesc}>{t('dashboard.dataDetailPage.localNormProjects')}</p>
                          </div>
                        </div>
                        <div className={cls(styles.panelItem)}>
                          <div className={styles.itemContent}>
                            <p className={styles.itemValue}>{statisticData.brand.default.projects}</p>
                            <p className={styles.itemDesc}>
                              {t('dashboard.dataDetailPage.brandNormProjects')}
                            </p>
                          </div>
                        </div>
                      </div>
                      <div className={styles.modeContentPanelItem}>
                        <div className={cls(styles.panelItem)}>
                          <div className={styles.itemContent}>
                            <p className={styles.itemValue}>{statisticData.base.default.norm === 0 ? '-' : statisticData.base.default.norm}</p>
                            <p className={styles.itemDesc}>{t('dashboard.dataDetailPage.localNorm')}</p>
                          </div>
                        </div>
                        <div className={styles.panelItem}>
                          <div className={styles.itemContent}>
                            <p className={styles.itemValue}>{statisticData.brand.default.norm === 0 ? '-' : statisticData.brand.default.norm}</p>
                            <p className={styles.itemDesc}>
                              {t('dashboard.dataDetailPage.brandNorm')}
                            </p>
                          </div>
                        </div>
                      </div>
                      <div className={styles.modeContentPanelItem}>
                        <div className={cls(styles.panelItem)}>
                          <div className={styles.itemContent}>
                            <p className={styles.itemValue}>
                              <span>{formatRatioAsPercentValue(statisticData.base.default.passRatio)}</span>
                              <span
                                style={{
                                  fontWeight: 400,
                                  fontSize: 16,
                                  color: '#2A2C3F',
                                  lineHeight: '26px',
                                }}
                              >
                                %
                              </span>
                            </p>
                            <p className={styles.itemDesc}>
                              {t('dashboard.dataDetailPage.localProjectPassRate')}
                            </p>
                          </div>
                        </div>
                        <div className={styles.panelItem}>
                          <div className={styles.itemContent}>
                            <p className={styles.itemValue}>
                              <span>{formatRatioAsPercentValue(statisticData.brand.default.passRatio)}</span>
                              <span
                                style={{
                                  fontWeight: 400,
                                  fontSize: 16,
                                  color: '#2A2C3F',
                                  lineHeight: '26px',
                                }}
                              >
                                %
                              </span>
                            </p>
                            <p className={styles.itemDesc}>
                              {t('dashboard.dataDetailPage.brandProjectPassRate')}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
            <div className={styles.divider} />
            <div className={styles.indexAnalysis}>
              <div className={styles.indexAnalysisTitle}>
                <div>
                  <span>
                    {t('dashboard.dataDetailPage.advertisingEffectivenessIndexDistribution')}
                  </span>
                  <SubAnalysisDescriptionPopover descriptions={desc2} />
                </div>
                {
                  brandNormStatus && (
                    <div className={styles.introBox}>
                      <div className={styles.introItem}>
                        <span className={styles.block}></span>
                        <span className={styles.text}>{t('dashboard.dataDetailPage.normProject')}</span>
                      </div>
                      <div className={styles.introItem}>
                        <span className={cls(styles.block, styles.brand)}></span>
                        <span className={styles.text}>{t('dashboard.dataDetailPage.brandNormProject')}</span>
                      </div>
                    </div>
                  )
                }
              </div>
              <div className={styles.indexAnalysisContent}>
                <div className={styles.chartBox} style={{ height: CHAR_HEIGHT }}>
                  <div className={styles.chartBoxContent} style={{ height: CHAR_HEIGHT * DELTA }}>
                    <div className={styles.chartBg}>
                      {interval.map((item, index) => {
                        return (
                          <div className={styles.chartBgItem} key={index}>
                            <span className={styles.chartBgItemValue1}>
                              {processPercent(item[1], 0)}
                            </span>
                            {index === 0 && (
                              <span className={styles.chartBgItemValue2}>
                                {processPercent(item[0], 0)}
                              </span>
                            )}
                          </div>
                        )
                      })}
                    </div>
                    <div className={styles.chartContent}>
                      {chartData.map((item) => {
                        return (
                          <div className={styles.chartItem} key={item.name}>
                            <div
                              className={cls(styles.chartItemBar, brandNormStatus && styles.slim)}
                              style={{ height: item.value * ratio }}
                            >
                              <div className={styles.chartItemValue}>{item.percent}</div>
                            </div>
                            {
                              brandNormStatus && (
                                <div className={cls(styles.chartItemBar, styles.brand, styles.slim)} style={{ height: item.brandValue * ratio }}>
                                  <div className={styles.chartItemValue}>{item.brandPercent}</div>
                                </div>
                              )
                            }
                            <span className={styles.chartItemName}>{item.name}</span>
                          </div>
                        )
                      })}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  )
}

function BaseDataItem(props: {
  type: string
  value: number | React.ReactNode
  desc: string
  style?: React.CSSProperties
}) {
  return (
    <div className={styles.baseDataItem} style={props.style}>
      <img
        className={styles.itemBg}
        src={commonUtils.getImagePath('statisticPage/item-bg-1.png')}
        alt=''
      />
      <AIcon width={40} height={40} type={props.type} />
      <div className={styles.itemContent}>
        <p className={styles.itemValue}>{props.value}</p>
        <p className={styles.itemDesc}>{props.desc}</p>
      </div>
    </div>
  )
}

export default observer(StatisticPage)
